<script setup lang="ts">
import { computed, defineAsyncComponent, ref } from 'vue'
import FairHomeChromeHeader from './FairHomeChromeHeader.vue'
import FairHomeChromeChips from './FairHomeChromeChips.vue'
import FairHomeChromeDock from './FairHomeChromeDock.vue'
import FairHomeHeroSection from './FairHomeHeroSection.vue'
import FairHomeCapsulesSection from './FairHomeCapsulesSection.vue'
import { useFairHomeStickyChrome } from './useFairHomeStickyChrome'

const FairHomeSelectionsSection = defineAsyncComponent(
  () => import('./FairHomeSelectionsSection.vue'),
)
const FairHomeBrandsSection = defineAsyncComponent(
  () => import('./FairHomeBrandsSection.vue'),
)
const FairHomeProductsSection = defineAsyncComponent(
  () => import('./FairHomeProductsSection.vue'),
)
const FairHomeFooterSection = defineAsyncComponent(
  () => import('./FairHomeFooterSection.vue'),
)
const FairHomeImageSearchFlow = defineAsyncComponent(
  () => import('./FairHomeImageSearchFlow.vue'),
)
const FairHomeDesktopImageSearchDialog = defineAsyncComponent(
  () => import('./FairHomeDesktopImageSearchDialog.vue'),
)
const FairHomeMegaMenuSheet = defineAsyncComponent(
  () => import('./FairHomeMegaMenuSheet.vue'),
)

const chipsRef = ref<InstanceType<typeof FairHomeChromeChips> | null>(null)
const menuEl = computed(() => chipsRef.value?.menuEl ?? null)

const audience = ref('women')
const megaMenuOpen = ref(false)
const megaMenuDepth = ref(0)
const { isPinned, searchOpen, megaCollapsed, setMegaPanelOpen } =
  useFairHomeStickyChrome(menuEl)

function openMegaMenuSheet() {
  megaMenuOpen.value = true
}

function closeMegaMenuSheet() {
  megaMenuOpen.value = false
  megaMenuDepth.value = 0
}
</script>

<template>
  <div
    class="fair-home-page"
    :class="{
      'fair-home-page--sticky-pinned': isPinned,
      'fair-home-page--mega-collapsed': megaCollapsed,
    }"
  >
    <!-- Page-level sticky header (not trapped inside Hero) -->
    <FairHomeChromeHeader
      v-model:audience="audience"
      v-model:search-open="searchOpen"
      :pinned="isPinned"
      :mega-collapsed="megaCollapsed"
      @mega-panel-open="setMegaPanelOpen(true)"
      @mega-panel-close="setMegaPanelOpen(false)"
    />

    <main class="fair-home-page__main">
      <FairHomeHeroSection />

      <!--
        Shell groups chips + capsules visually.
        Chips scroll normally; pin happens when they pass the top.
        Compact (<1024): chips hidden via CSS; dock replaces mega chrome.
      -->
      <div class="fair-home-capsules-shell">
        <FairHomeChromeChips
          ref="chipsRef"
          :handed-off="isPinned"
        />
        <FairHomeCapsulesSection />
      </div>

      <FairHomeSelectionsSection />
      <FairHomeBrandsSection />
      <FairHomeProductsSection />
      <FairHomeFooterSection />
    </main>

    <FairHomeChromeDock
      :search-sheet-open="megaMenuOpen"
      :suppressed="megaMenuOpen && megaMenuDepth > 0"
      @open-search-sheet="openMegaMenuSheet"
      @close-search-sheet="closeMegaMenuSheet"
    />

    <FairHomeMegaMenuSheet
      v-if="megaMenuOpen"
      v-model:visible="megaMenuOpen"
      @update:depth="megaMenuDepth = $event"
      @close="closeMegaMenuSheet"
    />

    <FairHomeImageSearchFlow />
    <FairHomeDesktopImageSearchDialog />
  </div>
</template>

<style scoped>
.fair-home-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  min-height: 100vh;
  overflow-x: clip;
  background: var(--fair-surface-page);
}

.fair-home-page__main {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  flex: 1 0 auto;
}

/*
  Dock clearance lives on the dark footer (not page padding) so the
  light page surface does not show as a white strip under the dock.
*/

/* Pull hero under sticky header so light header still sits on hero bg */
.fair-home-page :deep(.fair-home-hero) {
  margin-top: calc(-1 * var(--fair-home-chrome-header-height, 80px));
  padding-top: var(--fair-home-chrome-header-height, 80px);
}

.fair-home-capsules-shell {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--fair-color-white-100, #ffffff);
  border-radius: 0 0 var(--fair-radius-xl, 40px) var(--fair-radius-xl, 40px);
  overflow: visible;
}
</style>
